import React, { useState } from 'react';
import { 
  Calendar, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  Users, 
  FileText, 
  Activity,
  Dollar,
  BarChart2,
  MessageSquare,
  GitBranch,
  Shield,
  Settings
} from 'lucide-react';

// Composants réutilisables
const Card = ({ children, className }) => (
  <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
    {children}
  </div>
);

const ProgressBar = ({ value, max = 100, color = "bg-blue-500" }) => (
  <div className="w-full h-2 bg-gray-100 rounded-full">
    <div 
      className={`h-full rounded-full ${color}`} 
      style={{ width: `${(value / max) * 100}%` }}
    />
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    completed: "bg-green-100 text-green-800",
    inProgress: "bg-blue-100 text-blue-800",
    pending: "bg-yellow-100 text-yellow-800",
    delayed: "bg-red-100 text-red-800",
    critical: "bg-red-500 text-white",
    onTrack: "bg-green-500 text-white",
    attention: "bg-yellow-500 text-white"
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
};

export default function EnhancedProjectDashboard() {
  const [activeTab, setActiveTab] = useState('overview');

  // Données enrichies du projet
  const projectData = {
    metadata: {
      solutionId: "KG-2024-0123",
      clientName: "Banque Nationale de Paris",
      projectName: "Modernisation Core Banking",
      version: "2.3.0",
      environment: "Production",
    },
    contacts: {
      kgTechnologies: {
        projectManager: {
          name: "Sophie Martin",
          email: "s.martin@kg-tech.com",
          phone: "+33 6 12 34 56 78"
        },
        technicalLead: {
          name: "Thomas Bernard",
          email: "t.bernard@kg-tech.com",
          phone: "+33 6 23 45 67 89"
        }
      },
      client: {
        projectOwner: {
          name: "Jean Dupont",
          email: "j.dupont@bnp.fr",
          phone: "+33 1 23 45 67 89"
        },
        technicalContact: {
          name: "Marie Lambert",
          email: "m.lambert@bnp.fr",
          phone: "+33 1 34 56 78 90"
        }
      }
    },
    dates: {
      kickoff: "2024-01-15",
      plannedEnd: "2024-12-31",
      nextMilestone: "2024-03-31",
      lastDeployment: "2024-02-20",
      nextDeployment: "2024-03-15"
    },
    status: {
      overall: "onTrack",
      technical: "attention",
      budget: "onTrack",
      scope: "attention"
    },
    metrics: {
      completion: 45,
      budget: {
        planned: 1200000,
        spent: 450000,
        remaining: 750000,
        variance: -2.5
      },
      quality: {
        testCoverage: 87,
        bugs: {
          critical: 0,
          high: 2,
          medium: 5,
          low: 8
        },
        performanceScore: 92
      }
    },
    risks: [
      {
        id: 1,
        title: "Performance à l'échelle",
        level: "high",
        impact: "Dégradation possible des temps de réponse en charge",
        mitigation: "Tests de performance préventifs et optimisation continue",
        owner: "Thomas Bernard",
        dueDate: "2024-03-15"
      },
      {
        id: 2,
        title: "Intégration Legacy",
        level: "medium",
        impact: "Complexité de la migration des données historiques",
        mitigation: "POC de migration et plan de test détaillé",
        owner: "Marie Lambert",
        dueDate: "2024-04-01"
      }
    ],
    milestones: [
      {
        id: 1,
        name: "Phase 1: Infrastructure & Socle",
        deadline: "2024-03-31",
        completion: 75,
        status: "inProgress",
        description: "Mise en place de l'infrastructure cloud et du socle technique",
        deliverables: [
          { name: "Infrastructure Cloud", status: "completed", completion: 100 },
          { name: "Sécurité & IAM", status: "inProgress", completion: 80 },
          { name: "Base de données", status: "inProgress", completion: 90 },
          { name: "CI/CD Pipeline", status: "inProgress", completion: 85 }
        ],
        kpis: {
          availability: "99.99%",
          performance: "95%",
          security: "A+"
        }
      },
      {
        id: 2,
        name: "Phase 2: Core Features",
        deadline: "2024-06-30",
        completion: 30,
        status: "inProgress",
        description: "Développement des fonctionnalités core banking",
        deliverables: [
          { name: "Gestion des comptes", status: "inProgress", completion: 45 },
          { name: "Transactions", status: "inProgress", completion: 35 },
          { name: "Reporting", status: "pending", completion: 20 },
          { name: "API Gateway", status: "inProgress", completion: 40 }
        ],
        kpis: {
          testCoverage: "87%",
          codeQuality: "A",
          performance: "92%"
        }
      },
      {
        id: 3,
        name: "Phase 3: Advanced Features",
        deadline: "2024-09-30",
        completion: 10,
        status: "pending",
        description: "Fonctionnalités avancées et optimisations",
        deliverables: [
          { name: "Analytics", status: "pending", completion: 5 },
          { name: "Machine Learning", status: "pending", completion: 0 },
          { name: "Open Banking", status: "pending", completion: 10 },
          { name: "Mobile App", status: "pending", completion: 15 }
        ],
        kpis: {
          innovation: "High",
          scalability: "95%",
          reliability: "99.9%"
        }
      }
    ],
    recentActivities: [
      {
        date: "2024-02-20",
        type: "deployment",
        description: "Déploiement v2.3.0 en production",
        status: "success"
      },
      {
        date: "2024-02-18",
        type: "milestone",
        description: "Validation phase 1 - Infrastructure",
        status: "completed"
      },
      {
        date: "2024-02-15",
        type: "risk",
        description: "Nouveau risque identifié: Performance",
        status: "attention"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Enrichi */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col space-y-4">
            {/* Ligne 1: Informations principales */}
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold text-gray-900">
                  {projectData.metadata.projectName}
                </h1>
                <span className="text-gray-500">|</span>
                <div className="text-sm text-gray-600">
                  Solution ID: {projectData.metadata.solutionId}
                </div>
                <span className="text-gray-500">|</span>
                <div className="text-sm text-gray-600">
                  Version: {projectData.metadata.version}
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <StatusBadge status={projectData.status.overall} />
                <span className="text-sm text-gray-600">
                  Env: {projectData.metadata.environment}
                </span>
              </div>
            </div>

            {/* Ligne 2: Dates clés */}
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Calendar size={16} className="text-gray-400" />
                <span className="text-gray-600">Début: {projectData.dates.kickoff}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar size={16} className="text-gray-400" />
                <span className="text-gray-600">Fin prévue: {projectData.dates.plannedEnd}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Activity size={16} className="text-gray-400" />
                <span className="text-gray-600">Dernier déploiement: {projectData.dates.lastDeployment}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Activity size={16} className="text-gray-400" />
                <span className="text-gray-600">Prochain déploiement: {projectData.dates.nextDeployment}</span>
              </div>
            </div>

            {/* Ligne 3: Contacts */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">KG Technologies:</span>
                <span className="ml-2">{projectData.contacts.kgTechnologies.projectManager.name}</span>
                <span className="ml-2 text-gray-500">|</span>
                <span className="ml-2">{projectData.contacts.kgTechnologies.projectManager.email}</span>
              </div>
              <div>
                <span className="font-medium">Client:</span>
                <span className="ml-2">{projectData.contacts.client.projectOwner.name}</span>
                <span className="ml-2 text-gray-500">|</span>
                <span className="ml-2">{projectData.contacts.client.projectOwner.email}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation des onglets */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex space-x-4 border-b">
          {[
            { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart2 },
            { id: 'milestones', label: 'Jalons', icon: GitBranch },
            { id: 'risks', label: 'Risques', icon: AlertTriangle },
            { id: 'technical', label: 'Technique', icon: Settings },
            { id: 'communication', label: 'Communication', icon: MessageSquare },
            { id: 'security', label: 'Sécurité', icon: Shield }
          ].map(tab => (
            <button
              key={tab.id}
              className={`flex items-center space-x-2 px-4 py-2 border-b-2 font-medium ${
                activeTab === tab.id 
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon size={18} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* KPIs */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-blue-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Avancement</span>
                  <span className="text-lg font-bold text-blue-600">
                    {projectData.metrics.completion}%
                  </span>
                </div>
                <ProgressBar value={projectData.metrics.completion} color="bg-blue-500" />
              </Card>

              <Card className="bg-green-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Budget</span>
                  <span className="text-lg font-bold text-green-600">
                    {Math.round((projectData.metrics.budget.spent / projectData.metrics.budget.planned) * 100)}%
                  </span>
                </div>
                <ProgressBar 
                  value={projectData.metrics.budget.spent}
                  max={projectData.metrics.budget.planned}
                  color="bg-green-500"
                />
              </Card>

              <Card className="bg-purple-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Qualité</span>
                  <span className="text-lg font-bold text-purple-600">
                    {projectData.metrics.quality.testCoverage}%
                  </span>
                </div>
                <ProgressBar value={projectData.metrics.quality.testCoverage} color="bg-purple-500" />
              </Card>

              <Card className="bg-yellow-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Performance</span>
                  <span className="text-lg font-bold text-yellow-600">
                    {projectData.metrics.quality.performanceScore}%
                  </span>
                </div>
                <ProgressBar value={projectData.metrics.quality.performanceScore} color="bg-yellow-500" />
              </Card>
            </div>

            {/* Activités récentes */}
            {/* Suite du composant principal - Vue d'ensemble */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Activités Récentes</h2>
              <div className="space-y-4">
                {projectData.recentActivities.map((activity, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <Activity size={20} className="text-gray-400 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                        <span className="text-xs text-gray-500">{activity.date}</span>
                      </div>
                      <StatusBadge status={activity.status} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Résumé des jalons */}
            <div className="grid grid-cols-3 gap-6 mt-6">
              {projectData.milestones.map(milestone => (
                <Card key={milestone.id} className="border border-gray-200">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-medium text-gray-900">{milestone.name}</h3>
                    <StatusBadge status={milestone.status} />
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{milestone.description}</p>
                  <div className="flex justify-between items-center text-sm mb-2">
                    <span className="text-gray-500">Progression</span>
                    <span className="font-medium">{milestone.completion}%</span>
                  </div>
                  <ProgressBar value={milestone.completion} />
                  <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                    {Object.entries(milestone.kpis).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 p-2 rounded">
                        <div className="text-gray-500">{key}</div>
                        <div className="font-medium">{value}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'milestones' && (
          <div className="space-y-6">
            {projectData.milestones.map(milestone => (
              <Card key={milestone.id}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">
                      {milestone.name}
                    </h3>
                    <p className="text-sm text-gray-600">{milestone.description}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-500">Deadline</div>
                      <div className="font-medium">{milestone.deadline}</div>
                    </div>
                    <StatusBadge status={milestone.status} />
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Progression globale</span>
                    <span className="font-medium">{milestone.completion}%</span>
                  </div>
                  <ProgressBar value={milestone.completion} />
                </div>

                <div className="space-y-3">
                  {milestone.deliverables.map((deliverable, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center space-x-3">
                          <CheckCircle 
                            size={16} 
                            className={deliverable.status === 'completed' ? 'text-green-500' : 'text-gray-300'} 
                          />
                          <span className="font-medium">{deliverable.name}</span>
                        </div>
                        <StatusBadge status={deliverable.status} />
                      </div>
                      <ProgressBar value={deliverable.completion} />
                    </div>
                  ))}
                </div>

                <div className="mt-4 border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-2">KPIs</h4>
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(milestone.kpis).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-sm text-gray-500">{key}</div>
                        <div className="font-medium">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {activeTab === 'risks' && (
          <div className="space-y-4">
            {projectData.risks.map(risk => (
              <Card key={risk.id}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-medium text-gray-900">{risk.title}</h3>
                      <StatusBadge status={risk.level} />
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Impact: {risk.impact}</p>
                    <p className="text-sm text-gray-600">Mitigation: {risk.mitigation}</p>
                  </div>
                  <div className="text-right text-sm">
                    <div>
                      <span className="text-gray-500">Responsable:</span>
                      <span className="ml-2 font-medium">{risk.owner}</span>
                    </div>
                    <div className="mt-1">
                      <span className="text-gray-500">Échéance:</span>
                      <span className="ml-2 font-medium">{risk.dueDate}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Les autres onglets peuvent être ajoutés de manière similaire */}
      </div>
    </div>
  );
}